/**
 * EXPORT_SECTIONS registry — every exportable column. THE PII GATE DERIVES FROM THIS.
 * Loads schema/export-sections.json (single canonical source per ADR-0017),
 * Zod-validates at module load, FAIL CLOSED.
 *
 * Rule 3 lesson this file encodes (legacy CP-R1320): the FERPA disclosure gate once
 * hardcoded 13 column keys — six did not exist and the real identifiers were on no
 * list. The identifying-column set is COMPUTED here from the per-section pii flags.
 * The JSON's ferpaDerivation.piiColumnKeys is a REGRESSION FLOOR only: every key it
 * names must be covered by the derivation or the registry refuses to load — the
 * floor proves the derivation can fail; it never defines the set.
 */
import { z } from 'zod'
import { AppError } from '../errors/app-error'
import exportSectionsJson from '../../../../schema/export-sections.json'

export const ExportColumnSchema = z
  .object({
    t: z.enum(['roster', 'enrich', 'caseload']),
    k: z.string().min(1),
    l: z.string().min(1).optional(),
  })
  .strict()

export type ExportColumn = z.infer<typeof ExportColumnSchema>

export const ExportSectionSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    pii: z.boolean(),
    piiNote: z.string().optional(),
    primaryKey: z.string().optional(),
    primaryLabel: z.string().optional(),
    parentGroup: z.string().optional(),
    cols: z.array(ExportColumnSchema).min(1),
  })
  .strict()

export type ExportSection = z.infer<typeof ExportSectionSchema>

const BuiltinReportSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    sections: z.array(z.string().min(1)).min(1),
  })
  .strict()

export type BuiltinReport = z.infer<typeof BuiltinReportSchema>

const ExportSectionsFileSchema = z
  .object({
    description: z.string(),
    version: z.string(),
    target: z.string(),
    roadmapNote: z.string(),
    sections: z.array(ExportSectionSchema).min(5),
    builtinReports: z.array(BuiltinReportSchema),
    ferpaDerivation: z
      .object({
        note: z.string(),
        piiColumnKeys: z.array(z.string().min(1)).min(1),
      })
      .strict(),
  })
  .strict()

interface LoadedExportSections {
  sections: readonly ExportSection[]
  builtinReports: readonly BuiltinReport[]
  derivedPiiColumnKeys: ReadonlySet<string>
}

function loadExportSections(): LoadedExportSections {
  const parsed = ExportSectionsFileSchema.safeParse(exportSectionsJson)
  if (!parsed.success) {
    throw AppError.validation(
      'EXPORT_SECTIONS registry failed validation — refusing to load a malformed export catalog (fail closed)',
      parsed.error.issues,
    )
  }
  const { sections, builtinReports, ferpaDerivation } = parsed.data

  const sectionIds = new Set<string>()
  const parentGroups = new Set<string>()
  for (const s of sections) {
    if (sectionIds.has(s.id)) {
      throw AppError.validation(`EXPORT_SECTIONS registry: duplicate section id '${s.id}'`)
    }
    sectionIds.add(s.id)
    if (s.parentGroup) parentGroups.add(s.parentGroup)
  }

  for (const report of builtinReports) {
    for (const ref of report.sections) {
      if (!sectionIds.has(ref) && !parentGroups.has(ref)) {
        throw AppError.validation(
          `EXPORT_SECTIONS registry: builtin report '${report.id}' references '${ref}', which is neither a section id nor a parentGroup — an unresolvable export reference is a build defect`,
        )
      }
    }
  }

  // DERIVE the identifying-column set from the per-section pii flags. Never enumerate.
  const derived = new Set<string>()
  for (const s of sections) {
    if (s.pii) {
      for (const col of s.cols) derived.add(col.k)
    }
  }
  if (derived.size === 0) {
    throw AppError.validation(
      'EXPORT_SECTIONS registry: derivation produced ZERO identifying columns — a PII gate that covers nothing is not a gate (fail closed)',
    )
  }

  // The enumerated list is a REGRESSION FLOOR: every key it names must be covered.
  const uncovered = ferpaDerivation.piiColumnKeys.filter((k) => !derived.has(k))
  if (uncovered.length > 0) {
    throw AppError.validation(
      `EXPORT_SECTIONS registry: identifying column(s) [${uncovered.join(', ')}] are on the regression floor but NOT covered by the pii-section derivation — an identifying column outside the derived set is exactly the class the legacy FERPA gate missed. Fix the section's pii flag; do not shrink the floor.`,
    )
  }

  return {
    sections: Object.freeze(sections),
    builtinReports: Object.freeze(builtinReports),
    derivedPiiColumnKeys: derived,
  }
}

const loaded = loadExportSections()

/** Every export section, validated. Frozen. */
export const EXPORT_SECTIONS: readonly ExportSection[] = loaded.sections

/** Built-in report definitions (every section reference resolves). */
export const EXPORT_BUILTIN_REPORTS: readonly BuiltinReport[] = loaded.builtinReports

/** The DERIVED identifying-column set — the only source the PII gate may use. */
export const DERIVED_PII_COLUMN_KEYS: ReadonlySet<string> = loaded.derivedPiiColumnKeys

/** Whether a column key is identifying, per the derived set. Unknown keys are DENIED (treated as PII). */
export function isPiiColumn(key: string): boolean {
  const known = EXPORT_SECTIONS.some((s) => s.cols.some((c) => c.k === key))
  if (!known) return true // fail closed: a column the registry does not know is not exportable as non-PII
  return DERIVED_PII_COLUMN_KEYS.has(key)
}

/** Resolve a section by id. THROWS on unknown id. */
export function getExportSection(id: string): ExportSection {
  const s = EXPORT_SECTIONS.find((x) => x.id === id)
  if (!s) {
    throw AppError.notFound(`Unknown export section '${id}' — export columns resolve from the EXPORT_SECTIONS registry only`)
  }
  return s
}
